import { useEffect, useState } from 'react';
import { createItem, deleteItem, fetchList, updateItem } from './api';
import { canWriteEmpresaData } from '../../auth/session';
import CrudModal from '../../components/layout/CrudModal';
import { formatCurrencyBR, formatDecimalBR, normalizeCurrencyInputBR, normalizeDecimalInputBR, parseCurrencyBR, parseDecimalBR } from '../../utils/numberBR';

const EMPTY = {
  nome: '',
  tipo_operacao: 'incorporacao',
  status: 'planejamento',
  endereco_cidade: '',
  metragem_m2: '',
  valor_venda_previsto: '',
};

const moeda = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => `${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const ConstrutorObrasPage = () => {
  const canWrite = canWriteEmpresaData();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [benchmark, setBenchmark] = useState(null);
  const [obraBenchmarkId, setObraBenchmarkId] = useState('');

  const loadBenchmark = async (obraId = '') => {
    const params = obraId ? { obra_id: obraId } : {};
    const data = await fetchList('/api/construtor/benchmark', params);
    setBenchmark(data || null);
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchList('/api/construtor/obras', { q, status, page, pageSize: 10 });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
      const selected = obraBenchmarkId || data?.items?.[0]?.id || '';
      if (selected && !obraBenchmarkId) setObraBenchmarkId(selected);
      await loadBenchmark(selected);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const metragem = parseDecimalBR(form.metragem_m2);
    const valorVendaPrevisto = parseCurrencyBR(form.valor_venda_previsto);
    const payload = {
      ...form,
      metragem_m2: metragem == null ? null : metragem,
      valor_venda_previsto: valorVendaPrevisto == null ? null : valorVendaPrevisto,
    };
    if (editingId) {
      await updateItem('/api/construtor/obras', editingId, payload);
    } else {
      await createItem('/api/construtor/obras', payload);
    }
    setEditingId('');
    setForm(EMPTY);
    setShowFormModal(false);
    await load();
  };

  const onDelete = async (id) => {
    if (!window.confirm('Excluir obra?')) return;
    await deleteItem('/api/construtor/obras', id);
    await load();
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    setForm({
      nome: item.nome || '',
      tipo_operacao: item.tipo_operacao || 'incorporacao',
      status: item.status || 'planejamento',
      endereco_cidade: item.endereco_cidade || '',
      metragem_m2: item.metragem_m2 == null ? '' : formatDecimalBR(item.metragem_m2, 2),
      valor_venda_previsto: item.valor_venda_previsto == null ? '' : formatCurrencyBR(item.valor_venda_previsto),
    });
    setShowFormModal(true);
  };

  const onChangeBenchmarkObra = async (id) => {
    setObraBenchmarkId(id);
    await loadBenchmark(id);
  };

  const totalPages = Math.max(1, Math.ceil(total / 10));
  const historico = benchmark?.historico || {};
  const comparativo = benchmark?.comparativo || null;
  const benchmarkObras = Array.isArray(benchmark?.obras) ? benchmark.obras : [];

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Obras</h1>
          <p className="text-gray-600">Cadastro de empreendimentos e benchmark interno da operacao.</p>
        </div>
        {canWrite && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setEditingId('');
              setForm(EMPTY);
              setShowFormModal(true);
            }}
          >
            Incluir Obra
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between gap-3">
          <span>Benchmark Interno</span>
          <select className="form-select max-w-sm" value={obraBenchmarkId} onChange={(e) => onChangeBenchmarkObra(e.target.value)}>
            <option value="">Selecione uma obra</option>
            {benchmarkObras.map((obra) => <option key={obra.id} value={obra.id}>{obra.nome}</option>)}
          </select>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-gray-500">Custo medio por m2 (historico)</p>
            <p className="text-lg font-semibold">{moeda(historico.custo_medio_por_m2)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Margem media historica</p>
            <p className="text-lg font-semibold">{pct(historico.margem_media_pct)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Tempo medio de obra</p>
            <p className="text-lg font-semibold">{Number(historico.prazo_medio_dias || 0).toFixed(0)} dias</p>
          </div>
        </div>
        {comparativo && (
          <div className="card-body border-t grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-gray-500">Obra atual vs custo m2 historico</p>
              <p className="text-base font-medium">
                {moeda(comparativo.custo_por_m2_atual)} ({comparativo.delta_custo_por_m2_pct == null ? '-' : pct(comparativo.delta_custo_por_m2_pct)})
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Obra atual vs margem historica</p>
              <p className="text-base font-medium">
                {comparativo.margem_atual_pct == null ? '-' : pct(comparativo.margem_atual_pct)} ({comparativo.delta_margem_pct == null ? '-' : pct(comparativo.delta_margem_pct)})
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Obra atual vs prazo historico</p>
              <p className="text-base font-medium">
                {Number(comparativo.prazo_atual_dias || 0).toFixed(0)} dias ({comparativo.delta_prazo_pct == null ? '-' : pct(comparativo.delta_prazo_pct)})
              </p>
            </div>
          </div>
        )}
      </div>

      {canWrite && (
        <CrudModal
          open={showFormModal}
          title={editingId ? 'Editar Obra' : 'Nova Obra'}
          onClose={() => {
            setShowFormModal(false);
            setEditingId('');
            setForm(EMPTY);
          }}
          maxWidthClass="max-w-5xl"
        >
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
              <p className="text-sm font-semibold text-gray-800">Dados da Obra</p>
              <p className="text-xs text-gray-500 mt-1">Preencha os campos principais do empreendimento.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Nome da Obra</label>
                <input className="form-input" placeholder="Ex.: Residencial Aurora" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Tipo de Operacao</label>
                <select className="form-select" value={form.tipo_operacao} onChange={(e) => setForm({ ...form, tipo_operacao: e.target.value })}>
                  <option value="incorporacao">Incorporacao</option>
                  <option value="contrato">Contrato</option>
                </select>
              </div>
              <div>
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="planejamento">Planejamento</option>
                  <option value="em_execucao">Em execucao</option>
                  <option value="pausada">Pausada</option>
                  <option value="concluida">Concluida</option>
                  <option value="vendida">Vendida</option>
                </select>
              </div>
              <div>
                <label className="form-label">Cidade</label>
                <input className="form-input" placeholder="Ex.: Sao Paulo" value={form.endereco_cidade} onChange={(e) => setForm({ ...form, endereco_cidade: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Metragem (m2)</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={form.metragem_m2}
                  onChange={(e) => setForm({ ...form, metragem_m2: normalizeDecimalInputBR(e.target.value, 2) })}
                />
              </div>
              <div>
                <label className="form-label">Valor de Venda Previsto (R$)</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={form.valor_venda_previsto}
                  onChange={(e) => setForm({ ...form, valor_venda_previsto: normalizeCurrencyInputBR(e.target.value) })}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-4">
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  setShowFormModal(false);
                  setEditingId('');
                  setForm(EMPTY);
                }}
              >
                Cancelar
              </button>
              <button className="btn-primary" type="submit">{editingId ? 'Atualizar Obra' : 'Salvar Obra'}</button>
            </div>
          </form>
        </CrudModal>
      )}

      <div className="card">
        <div className="card-header flex items-center gap-3">
          <input className="form-input max-w-sm" placeholder="Buscar obra..." value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="form-select max-w-xs" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos status</option>
            <option value="planejamento">Planejamento</option>
            <option value="em_execucao">Em execucao</option>
            <option value="pausada">Pausada</option>
            <option value="concluida">Concluida</option>
            <option value="vendida">Vendida</option>
          </select>
          <button className="btn-secondary" onClick={load}>Buscar</button>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Cidade</th>
                  <th>M2</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>{it.nome}</td>
                    <td>{it.tipo_operacao}</td>
                    <td>{it.status}</td>
                    <td>{it.endereco_cidade || '-'}</td>
                    <td>{it.metragem_m2 == null ? '-' : Number(it.metragem_m2).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</td>
                    <td>
                      {canWrite && <button className="btn-secondary mr-2" onClick={() => onEdit(it)}>Editar</button>}
                      {canWrite && <button className="btn-danger" onClick={() => onDelete(it.id)}>Excluir</button>}
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-gray-500 py-6">Sem dados</td></tr>
                )}
                {loading && (
                  <tr><td colSpan={6} className="text-center text-gray-500 py-6">Carregando...</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-body border-t flex items-center justify-between">
          <span className="text-sm text-gray-600">Pagina {page} de {totalPages} ({total} registros)</span>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</button>
            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Proxima</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstrutorObrasPage;
